'use client';

// =============================================================================
// PostHogProvider — client-side init PostHog один раз при mount root layout.
//
// Init lazy через dynamic import — bundle posthog-js (~50KB gzipped) не
// блокирует first paint. Pageview трекается на каждом mount + при изменении
// pathname (Next.js router).
//
// Ловим getInitData() и identify только если телеграм-юзер реально есть.
// В dev-режиме без TG identify не происходит — все events пишутся под
// anonymous distinct_id, и ни одна Person не создаётся (because
// person_profiles: 'identified_only').
// =============================================================================

import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

import { getPostHogConfig, identifyTgUser, setPostHogInstance, trackEvent, trackPageView } from '@/lib/analytics';
import { getDeviceFingerprint } from '@/lib/fingerprint';
import { getWebApp } from '@/lib/tma';

let initStarted = false;

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  // Init один раз на жизнь страницы.
  useEffect(() => {
    if (initStarted) return;
    initStarted = true;

    // Прогреваем fingerprint в фоне (≈100ms). Не ждём его — следующий же
    // authed запрос (api.me) подхватит из localStorage если успел.
    void getDeviceFingerprint();

    // Dynamic import чтобы posthog не попал в initial bundle.
    void import('posthog-js').then(({ default: posthog }) => {
      const { key, host } = getPostHogConfig();
      posthog.init(key, {
        api_host: host,
        // EU host обслуживает и static assets, и capture endpoint.
        ui_host: 'https://eu.posthog.com',
        autocapture: false,
        capture_pageview: false, // делаем manually через pathname-effect ниже
        capture_pageleave: true,
        disable_session_recording: true,
        // 3rd-party cookies заблокированы в TG WebView → используем localStorage.
        persistence: 'localStorage',
        // Не создаём person profiles для anonymous юзеров (privacy + free tier).
        person_profiles: 'identified_only',
        // Defence-in-depth: ещё одна линия санитизации перед отправкой.
        before_send: (event) => {
          if (!event) return null;
          if (event.properties) {
            for (const k of Object.keys(event.properties)) {
              if (/init.?data|tx.?hash|seed|secret|token/i.test(k)) {
                event.properties[k] = '[REDACTED]';
              }
            }
            // Strip initData из $current_url.
            if (typeof event.properties.$current_url === 'string') {
              event.properties.$current_url = event.properties.$current_url.replace(
                /([?&])initData=[^&]+/g,
                '$1initData=[REDACTED]',
              );
            }
          }
          return event;
        },
        // Не отслеживаем browsers/extensions/bots.
        opt_out_capturing_by_default: false,
        loaded: (ph) => {
          setPostHogInstance(ph);
          // Identify сразу — TG SDK уже загружен к этому моменту (script
          // в layout.tsx с async, но в практике инжектируется до hydrate).
          const tg = getWebApp();
          if (tg?.initDataUnsafe?.user) {
            identifyTgUser({
              id: tg.initDataUnsafe.user.id,
              language_code: tg.initDataUnsafe.user.language_code,
            });
          }
          trackEvent('app_opened', {
            in_telegram: Boolean(tg?.initData),
            color_scheme: tg?.colorScheme ?? 'unknown',
          });
        },
      });
    }).catch(() => {
      // PostHog не критичен. Если CDN/network убит — silent fail, юзер ничего не теряет.
    });
  }, []);

  // Manual pageview трекинг при изменении pathname. capture_pageview: false
  // в init выше → этот effect единственный источник $pageview events.
  useEffect(() => {
    if (!pathname) return;
    trackPageView();
  }, [pathname]);

  return <>{children}</>;
}
